import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import SignupBackground from '@/assets/login_background.png';
import { TermsCheckbox } from '@/shared/components/invite-signup';
import { checkEmailAvailability, publicSignup } from '../api/authApi';
import { useContactCardOpen } from '@/features/contact-card-share/hooks/useContactCardOpen';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEBOUNCE_MS = 300;

const getRecaptchaSiteKey = () => import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Feature 071: the contact-card email's CTA lands here with ?share=<deliveryId>.
  useContactCardOpen();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const recaptchaSiteKey = getRecaptchaSiteKey();

  useEffect(() => {
    setEmail(searchParams.get('email') ?? '');
  }, [searchParams]);

  const checkEmail = useCallback(async (value: string) => {
    if (!value || !EMAIL_REGEX.test(value)) {
      setEmailTaken(false);
      return;
    }

    try {
      const result = await checkEmailAvailability(value);
      setEmailTaken(result.exists);
    } catch {
      setEmailTaken(false);
    }
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => checkEmail(email), DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [checkEmail, email]);

  const normalizedEmail = email.trim().toLowerCase();
  const canSubmit = Boolean(
    firstName.trim() &&
      lastName.trim() &&
      normalizedEmail &&
      EMAIL_REGEX.test(normalizedEmail) &&
      termsAgreed &&
      !emailTaken &&
      !isSubmitting &&
      recaptchaSiteKey,
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const recaptchaToken = (await recaptchaRef.current?.executeAsync()) ?? '';
      await publicSignup({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: normalizedEmail,
        recaptchaToken,
      });
      navigate(`/?email=${encodeURIComponent(normalizedEmail)}&autoLogin=true`);
    } catch (error) {
      recaptchaRef.current?.reset();
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to create your account. Please try again.';
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="min-h-screen bg-[#026857]">
        <main className="md:container flex min-h-screen items-center gap-16 px-4 py-12 max-md:flex-col md:px-6">
          <section className="w-full max-w-[34rem] py-8">
            <h1 className="font-recoleta text-3xl text-white md:text-4xl">
              Create your bQuest account
            </h1>
            <p className="font-termina py-6 text-white max-md:text-xl">
              Create your account to access bQuest planning tools, resources,
              and care guidance.
            </p>

            <p className="font-termina text-white">
              Have an account?{' '}
              <Link className="font-termina text-white underline" to="/login">
                Sign In
              </Link>
            </p>

            {apiError && (
              <p className="font-termina py-4 font-bold text-red-500">
                {apiError}
              </p>
            )}

            {!recaptchaSiteKey ? (
              <ConfigurationError />
            ) : (
              <form className="flex flex-col gap-6 py-12" onSubmit={handleSubmit}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={recaptchaSiteKey}
                />

                <FormField label="First Name">
                  <input
                    className="rounded-3xl px-5 py-3 font-termina text-base md:w-4/5"
                    type="text"
                    placeholder="Your first name"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    required
                  />
                </FormField>

                <FormField label="Last Name">
                  <input
                    className="rounded-3xl px-5 py-3 font-termina text-base md:w-4/5"
                    type="text"
                    placeholder="Your last name"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    required
                  />
                </FormField>

                <FormField label="Email Address">
                  <input
                    className="rounded-3xl px-5 py-3 font-termina text-base md:w-4/5"
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value.trim());
                      setApiError(null);
                    }}
                    required
                  />
                  {emailTaken && <EmailTakenError />}
                </FormField>

                <TermsCheckbox checked={termsAgreed} onChange={setTermsAgreed} />

                <button
                  type="submit"
                  className="w-max cursor-pointer rounded-3xl bg-[#E2A807] px-6 py-3 font-termina font-black uppercase transition-all duration-150 hover:bg-[#c99006] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#E2A807] disabled:active:scale-100"
                  disabled={!canSubmit}
                >
                  {isSubmitting ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            )}
          </section>

          <section className="w-full max-w-[34rem] rounded-2xl p-2 max-md:mb-12">
            <img
              src={SignupBackground}
              alt="signup_background"
              className="w-full rounded-2xl bg-cover bg-center"
            />
          </section>
        </main>
      </div>
    </div>
  );
}

const FormField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col">
    <label className="pb-3 font-termina uppercase text-white max-md:text-xl">
      {label}
    </label>
    {children}
  </div>
);

const EmailTakenError = () => (
  <div className="mt-3">
    <p className="font-termina text-white">
      Already have an account?{' '}
      <Link className="font-termina text-white underline" to="/login">
        Sign In
      </Link>
    </p>
    <p className="font-termina font-bold text-red-500">
      Looks like this email is already taken. Please try a different one.
    </p>
  </div>
);

const ConfigurationError = () => (
  <div className="py-12">
    <div className="rounded-3xl border border-red-400 bg-red-100 px-4 py-3 text-red-700">
      <p className="font-termina font-bold">Configuration Error</p>
      <p className="font-termina text-sm">
        reCAPTCHA is not properly configured. Please contact support.
      </p>
    </div>
  </div>
);

